import { useState } from "react";
import { Card, Row, Col, Statistic, Button } from "antd";
import {
  Line,
  LineChart,
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
  Legend,
} from "recharts";
import { LeftOutlined, RightOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { IntelligenceDto } from "dtos/application.dto";
import { Payload } from "recharts/types/component/DefaultTooltipContent";
import { twColors } from "utils/constants";
import { formatMoney } from "utils/functions";

type OverViewProps = {
  data: IntelligenceDto;
};

const MonthlyAnalytics = ({ data }: OverViewProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [volatilityPage, setVolatilityPage] = useState(0);
  const itemsPerPage = 20;

  const totalPages = Math.ceil(data.monthly_profit_summary.length / itemsPerPage);
  const currentData = data?.monthly_profit_summary?.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  const volatilityData = data.monthly_daily_volatility ?? [];
  const volatilityTotalPages = Math.ceil(volatilityData.length / itemsPerPage || 1);
  const currentVolatilityData = volatilityData
    .slice(volatilityPage * itemsPerPage, (volatilityPage + 1) * itemsPerPage)
    .map((item) => ({
      ...item,
      daily_profit_std: item.daily_profit_std ?? 0,
      daily_revenue_std: item.daily_revenue_std ?? 0,
    }));

  const totalPage = Math.ceil((data.net_monthly_profit?.length ?? 0) / itemsPerPage);
  const currentMonthlyData = data?.net_monthly_profit?.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage,
  );

  return (
    <div className="flex flex-col gap-6">
      <Row gutter={16}>
        <Col span={6}>
          <Card className="border-1 border-gray-300 ">
            <Statistic
              title="Monthly Profit Std"
              value={data.net_monthly_profit_std ?? 0}
              valueStyle={{
                color: Number(data?.net_monthly_profit_std ?? 0) > 0 ? twColors.green600 : twColors.red600,
              }}
              formatter={(value) => `${formatMoney(Number(value), false, false)}`}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="border-1 border-gray-300 ">
            <Statistic
              title="Revenue Std"
              value={data.net_monthly_revenue_std ?? 0}
              valueStyle={{
                color: Number(data?.net_monthly_revenue_std ?? 0) > 0 ? twColors.green600 : twColors.red600,
              }}
              formatter={(value) => `${formatMoney(Number(value), false, false)}`}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="border-1 border-gray-300 ">
            <Statistic
              title="Profit Variance"
              value={data.net_monthly_profit_variance ?? 0}
              valueStyle={{
                color: Number(data?.net_monthly_profit_variance ?? 0) > 0 ? twColors.green600 : twColors.red600,
              }}
              formatter={(value) => `${formatMoney(Number(value), false, false)}`}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="border-1 border-gray-300 ">
            <Statistic
              title="Revenue Variance"
              value={data.net_monthly_revenue_variance ?? 0}
              valueStyle={{
                color: Number(data?.net_monthly_revenue_variance ?? 0) > 0 ? twColors.green600 : twColors.red600,
              }}
              formatter={(value) => `${formatMoney(Number(value), false, false)}`}
            />
          </Card>
        </Col>
      </Row>

      {/* Monthly Profit Summary Chart */}
      <Card
        className="border-1 border-gray-300 "
        title="Monthly Profit Summary"
        extra={
          <div className="flex gap-2 items-center text-gray-500 text-xs">
            <span>
              Page {currentPage + 1} of {totalPages}
            </span>
            <Button
              icon={<LeftOutlined />}
              size="small"
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
            />
            <Button
              icon={<RightOutlined />}
              size="small"
              onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage === totalPages - 1}
            />
          </div>
        }
      >
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={currentData}>
              <CartesianGrid strokeDasharray="3 3" stroke={twColors.gray500} />
              <XAxis
                dataKey={(item) => `${item.year}-${String(item.month).padStart(2, "0")}`}
                tick={{ fill: twColors.gray500, fontSize: 10 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis tick={{ fill: twColors.gray500, fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #d9d9d9",
                  borderRadius: "6px",
                }}
                formatter={(value, name) => [`$${Number(value).toLocaleString()}`, name]}
              />
              <Legend />
              <Bar dataKey="total_revenue" name="Revenue" fill={twColors.blue500} />
              <Bar dataKey="total_expenses" name="Expenses" fill={twColors.red500} />
              <Bar dataKey="net_profit" name="Net Profit" fill={twColors.green500} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Bottom Section: Volatility + Profit Analysis */}
      <div className="flex flex-col lg:flex-row w-full gap-4">
        {/* Daily Volatility Chart */}
        <Card
          className="w-full lg:w-1/2"
          title="Monthly Daily Volatility"
          extra={
            volatilityData.length > 0 && (
              <div className="flex gap-2 items-center text-gray-500 text-xs">
                <span>
                  Page {volatilityPage + 1} of {volatilityTotalPages}
                </span>
                <Button
                  icon={<LeftOutlined />}
                  size="small"
                  onClick={() => setVolatilityPage(Math.max(0, volatilityPage - 1))}
                  disabled={volatilityPage === 0}
                />
                <Button
                  icon={<RightOutlined />}
                  size="small"
                  onClick={() => setVolatilityPage(Math.min(volatilityTotalPages - 1, volatilityPage + 1))}
                  disabled={volatilityPage === volatilityTotalPages - 1}
                />
              </div>
            )
          }
        >
          {volatilityData.length === 0 ? (
            <div className="text-center py-12 text-gray-400">Data not available</div>
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={currentVolatilityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={twColors.gray500} />
                  <XAxis
                    dataKey={(item) => `${item.year}-${String(item.month).padStart(2, "0")}`}
                    tick={{ fill: twColors.gray500, fontSize: 10 }}
                  />
                  <YAxis tick={{ fill: twColors.gray500, fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #d9d9d9",
                      borderRadius: "6px",
                    }}
                    formatter={(value) => (value === 0 ? "N/A" : value)}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="daily_profit_std"
                    stroke={twColors.blue600}
                    name="Profit Std"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="daily_revenue_std"
                    stroke={twColors.green600}
                    name="Revenue Std"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        {/* Monthly Profit Analysis */}
        <Card
          className="w-full lg:w-1/2"
          title="Monthly Profit Analysis"
          extra={
            <div className="flex gap-2 items-center text-gray-500 text-xs">
              <span>
                Page {currentPage + 1} of {totalPage}
              </span>
              <Button
                icon={<LeftOutlined />}
                size="small"
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
              />
              <Button
                icon={<RightOutlined />}
                size="small"
                onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage === totalPage - 1}
              />
            </div>
          }
        >
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={currentMonthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke={twColors.gray500} />
                <XAxis
                  dataKey={(item) => `${item.year}-${String(item.month).padStart(2, "0")}`}
                  tick={{ fill: twColors.gray500, fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis tick={{ fill: twColors.gray500, fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #d9d9d9",
                    borderRadius: "6px",
                  }}
                  formatter={(value: number, _name: string, props: Payload<number, string>) => {
                    const payload = props.payload as { outcome: "profit" | "loss"; percentage: number };
                    return [
                      `$${Number(value).toLocaleString()}`,
                      `${payload.outcome === "profit" ? "Profit" : "Loss"} (${payload.percentage.toFixed(1)}%)`,
                    ];
                  }}
                />
                <Legend />
                <Bar dataKey="net_value" radius={[2, 2, 0, 0]}>
                  {currentMonthlyData?.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.outcome === "profit" ? twColors.green500 : twColors.red500}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MonthlyAnalytics;
